import { useState } from "react";
import Layout from "../../layout";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "../../components/basic-reports/ui/tabs";
import EnergyReports from "../../components/basic-reports/reports/EnergyReports";
import DemandReports from "../../components/basic-reports/reports/DemandReports";
import PeakAnalysis from "../../components/basic-reports/reports/PeakAnalysis";
import PowerQuality from "../../components/basic-reports/reports/PowerQuality";
import TamperFraud from "../../components/basic-reports/reports/TamperFraud";
import Operations from "../../components/basic-reports/reports/Operations";
import DateRangeFilter from "../../components/basic-reports/reports/DateRangeFilter";
import {
  Zap,
  TrendingUp,
  Clock,
  Gauge,
  ShieldAlert,
  Settings,
  Activity,
  Search,
} from "lucide-react";
import { Badge } from "../../components/basic-reports/ui/badge";
import {
  ReportDataProvider,
  useReportData,
} from "../../contexts/ReportDataContext";

const DEFAULT_CUSTOMER_ID = "f28683d0-ce76-11f0-a804-d33aca43567e";

const MeterCountBadge = () => {
  const { meterCount, loading, progress } = useReportData();
  return (
    <div className="flex items-center gap-2">
      <Badge variant="secondary" className="flex items-center gap-1">
        <Activity className="w-3 h-3" />
        {loading ? "Loading..." : `${meterCount.toLocaleString()} meters`}
      </Badge>
      {loading && progress && (
        <span className="text-xs text-muted-foreground animate-pulse">
          {progress}
        </span>
      )}
    </div>
  );
};

const Reports = () => {
  const [dateRange, setDateRange] = useState<{ from: Date; to: Date }>({
    from: new Date(Date.now() - 6 * 24 * 60 * 60 * 1000),
    to: new Date(),
  });
  const [customerIdInput, setCustomerIdInput] = useState(DEFAULT_CUSTOMER_ID);
  const [activeCustomerId, setActiveCustomerId] = useState(DEFAULT_CUSTOMER_ID);

  const handleCustomerSearch = () => {
    const trimmed = customerIdInput.trim();
    if (trimmed) {
      setActiveCustomerId(trimmed);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      handleCustomerSearch();
    }
  };

  return (
    <Layout>
      <div className="py-1 px-6">
        {/* Header Section */}
        <div className="flex flex-col gap-4 mb-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              {/* Customer ID Input */}
              <div className="flex items-center gap-2">
                <label
                  htmlFor="customer-id"
                  className="text-sm font-medium text-muted-foreground whitespace-nowrap"
                >
                  Customer ID:
                </label>
                <div className="flex items-center">
                  <input
                    id="customer-id"
                    type="text"
                    value={customerIdInput}
                    onChange={(e) => setCustomerIdInput(e.target.value)}
                    onKeyDown={handleKeyDown}
                    placeholder="Enter Customer UUID"
                    className="h-9 px-3 py-1 text-sm border border-input rounded-l-md bg-background 
                             focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-1
                             placeholder:text-muted-foreground w-[340px]"
                  />
                  <button
                    onClick={handleCustomerSearch}
                    className="h-9 px-3 border border-l-0 border-input rounded-r-md 
                             bg-[#1064ed] text-primary-foreground hover:bg-primary/90
                             transition-colors flex items-center gap-1 text-sm"
                  >
                    <Search className="w-3.5 h-3.5" />
                  </button>
                </div>
              </div>
            </div>
            <DateRangeFilter onDateChange={setDateRange} />
          </div>

          <div className="flex items-center justify-between">
            <div>
              <ReportDataProvider
                customerId={activeCustomerId}
                dateRange={dateRange}
              >
                <MeterCountBadge />
              </ReportDataProvider>
              <p className="text-muted-foreground mt-1 text-sm">
                Aggregated analytics from smart meter register data
              </p>
            </div>
          </div>
        </div>

        {/* Wrap all report tabs with the provider */}
        <ReportDataProvider customerId={activeCustomerId} dateRange={dateRange}>
          <Tabs defaultValue="energy" className="w-full">
            <TabsList className="inline-flex gap-6 mb-6 h-auto p-0 bg-transparent border-b border-border rounded-none w-full">
              <TabsTrigger
                value="energy"
                className="flex items-center gap-2 py-3 px-1 rounded-none border-b-2 border-transparent data-[state=active]:border-primary data-[state=active]:text-primary data-[state=active]:bg-transparent data-[state=active]:shadow-none bg-transparent text-muted-foreground hover:text-foreground"
              >
                <Zap className="w-4 h-4" />
                <span>Energy</span>
              </TabsTrigger>
              <TabsTrigger
                value="demand"
                className="flex items-center gap-2 py-3 px-1 rounded-none border-b-2 border-transparent data-[state=active]:border-primary data-[state=active]:text-primary data-[state=active]:bg-transparent data-[state=active]:shadow-none bg-transparent text-muted-foreground hover:text-foreground"
              >
                <TrendingUp className="w-4 h-4" />
                <span>Demand</span>
              </TabsTrigger>
              <TabsTrigger
                value="peak"
                className="flex items-center gap-2 py-3 px-1 rounded-none border-b-2 border-transparent data-[state=active]:border-primary data-[state=active]:text-primary data-[state=active]:bg-transparent data-[state=active]:shadow-none bg-transparent text-muted-foreground hover:text-foreground"
              >
                <Clock className="w-4 h-4" />
                <span>Peak Analysis</span>
              </TabsTrigger>
              <TabsTrigger
                value="quality"
                className="flex items-center gap-2 py-3 px-1 rounded-none border-b-2 border-transparent data-[state=active]:border-primary data-[state=active]:text-primary data-[state=active]:bg-transparent data-[state=active]:shadow-none bg-transparent text-muted-foreground hover:text-foreground"
              >
                <Gauge className="w-4 h-4" />
                <span>Power Quality</span>
              </TabsTrigger>
              <TabsTrigger
                value="tamper"
                className="flex items-center gap-2 py-3 px-1 rounded-none border-b-2 border-transparent data-[state=active]:border-primary data-[state=active]:text-primary data-[state=active]:bg-transparent data-[state=active]:shadow-none bg-transparent text-muted-foreground hover:text-foreground"
              >
                <ShieldAlert className="w-4 h-4" />
                <span>Tamper/Fraud</span>
              </TabsTrigger>
              <TabsTrigger
                value="operations"
                className="flex items-center gap-2 py-3 px-1 rounded-none border-b-2 border-transparent data-[state=active]:border-primary data-[state=active]:text-primary data-[state=active]:bg-transparent data-[state=active]:shadow-none bg-transparent text-muted-foreground hover:text-foreground"
              >
                <Settings className="w-4 h-4" />
                <span>Operations</span>
              </TabsTrigger>
            </TabsList>

            <TabsContent value="energy">
              <EnergyReports />
            </TabsContent>
            <TabsContent value="demand">
              <DemandReports />
            </TabsContent>
            <TabsContent value="peak">
              <PeakAnalysis />
            </TabsContent>
            <TabsContent value="quality">
              <PowerQuality />
            </TabsContent>
            <TabsContent value="tamper">
              <TamperFraud />
            </TabsContent>
            <TabsContent value="operations">
              <Operations />
            </TabsContent>
          </Tabs>
        </ReportDataProvider>
      </div>
    </Layout>
  );
};

export default Reports;
